import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useState } from "react";
import isEmail from "validator/lib/isEmail";
import DuplicateContactCard from "~/components/duplicate_contact_card";
import AllowOrgDuplicateCheckBox from "~/components/forms/allow_org_duplicate_checkbox";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import ErrorMessageWithIcon from "~/components/forms/ErrorMessageWithIcon";
import { EXTERNAL_ROLE_OPTIONS } from "~/constants/dropdowns/RoleOptions";
import { apiFetch } from "~/lib/fetchClient";

const AddTeamMemberForm = ({ title, onSave, onCancel }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [duplicatesFoundInOrg, setDuplicatesFoundInOrg] = useState(false);
  const [duplicateOrgAccounts, setDuplicateOrgAccounts] = useState([]);
  const [allowDuplicatesInOrg, setAllowDuplicatesInOrg] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!firstName) {
      newErrors.first_name = "This field can not be empty";
    }
    if (!lastName) {
      newErrors.last_name = "This field can not be empty";
    }

    if (email) {
      if (!isEmail(email)) {
        newErrors.email = "The provided email is invalid";
      }
    } else {
      newErrors.email = [
        "Please add an email address to create this record so that there is a way to match and update the data later and this email will function as their username",
      ];
    }

    if (!role) {
      newErrors.role = "This field can not be empty";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) {
      return;
    }

    setSending(true);
    setDuplicatesFoundInOrg(false);
    setDuplicateOrgAccounts([]);
    setErrors({});

    if (!validate()) {
      setSending(false);
      return;
    }

    try {
      const body = await apiFetch.post("/team/members", {
        json: {
          user: { first_name: firstName, last_name: lastName, notify: true },
          role: { name: role },
          lead: {
            lead_emails_attributes: [
              {
                id: null,
                email,
                email_type: "office",
                main: true,
                _destroy: false,
              },
            ],
            allow_duplicates_in_org: allowDuplicatesInOrg,
          },
        },
      });
      onSave({ value: body.id.toString() });
    } catch (error) {
      const responseBody = typeof error === "object" ? error : {};
      if (responseBody.duplicate_emails) {
        const dup = responseBody.duplicate_emails[0];
        const newErrors = {
          ...(responseBody.errors ?? {}),
          email: "It looks like a contact with this email already exists.",
          duplicate: { context: dup.context, match: dup.match },
        };

        if (dup.context === "org") {
          setDuplicateOrgAccounts([`${dup.match.name} - Email: ${dup.value}`]);
          setDuplicatesFoundInOrg(true);
        }
        setErrors(newErrors);
      } else {
        setErrors(responseBody.errors ?? responseBody ?? {});
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card mt15">
      <div className="card-block">
        <h5 className="mb15">{title}</h5>
        <div className="field-group mb15">
          <label htmlFor="add_team_member_first_name" className="d-block">
            First Name
          </label>
          <input
            type="text"
            id="add_team_member_first_name"
            className={`form-control ${errors.first_name ? "has-error" : ""}`}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {errors.first_name && (
            <ErrorMessageWithIcon message={errors.first_name} />
          )}
        </div>
        <div className="field-group mb15">
          <label htmlFor="add_team_member_last_name" className="d-block">
            Last Name
          </label>
          <input
            type="text"
            id="add_team_member_last_name"
            className={`form-control ${errors.last_name ? "has-error" : ""}`}
            onChange={(e) => setLastName(e.target.value)}
          />
          {errors.last_name && (
            <ErrorMessageWithIcon message={errors.last_name} />
          )}
        </div>
        <div className="field-group mb15">
          <label htmlFor="add_team_member_email" className="d-block">
            Email
          </label>
          <input
            type="text"
            id="add_team_member_email"
            className={`form-control ${errors.email ? "has-error" : ""}`}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <ErrorMessageWithIcon message={errors.email} />}
          {errors.duplicate && errors.duplicate.context === "team" && (
            <DuplicateContactCard duplicate={errors.duplicate} />
          )}
          {duplicatesFoundInOrg && (
            <AllowOrgDuplicateCheckBox
              checked={allowDuplicatesInOrg}
              duplicate_org_accounts={duplicateOrgAccounts}
              onChange={setAllowDuplicatesInOrg}
            />
          )}
        </div>
        <div className="field-group">
          <label htmlFor="add_team_member_role" className="d-block">
            Role
          </label>
          <Select
            id="add_team_member_role"
            isInvalid={!!errors.role}
            options={EXTERNAL_ROLE_OPTIONS}
            value={role}
            onChange={(opt) => setRole(opt?.value ?? "")}
            isSearchable={false}
          />
          {errors.role && <ErrorMessageWithIcon message={errors.role} />}
        </div>

        <div className="mt15">
          <button
            type="button"
            className="btn btn-primary mr5"
            disabled={sending}
            onClick={sending ? undefined : handleSubmit}
          >
            {sending ? (
              <>
                Saving <FontAwesomeIcon icon="far fa-spinner" pulse />
              </>
            ) : (
              "Save"
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

AddTeamMemberForm.propTypes = {
  title: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AddTeamMemberForm;
